import React from "react";
import {Avatar, Box, Flex, Grid, Title, Text, Button, rem, Badge, ActionIcon} from "@mantine/core";
import {IconChevronRight} from "@tabler/icons-react";
import { summarizeAvailability } from "lib/utils";

interface Props {
    id: number
    name: string
    fullAddress: string
    daysWaiting: number
    age: number
    gender: string
    genderPreference: string
    dementiaPresent: boolean
    volunteerMatches: number
    smoker: boolean
    additionalLanguages: string
    availability: string
    salesforceId: string
    chn: string
    rha: string
    onClick: () => void
}

export default function PersonCard(props: Props) {
    const genderPreference =  props.genderPreference.charAt(0).toUpperCase() + props.genderPreference.slice(1)
    const hasDementia = props.dementiaPresent ? 'Dementia' : 'No Dementia';
    const additionalLanguages = props.additionalLanguages?.split(',').map(lang => `${lang.trim()} L.`) ?? [];
    const availability = summarizeAvailability(props.availability);

    const generalInformationItems = [
        props.smoker ? 'Smoker' : 'Non Smoker'
        , `${genderPreference} vol.`, hasDementia, ...additionalLanguages].filter(Boolean);
    const locationItems = [props.rha, props.chn];

    return (
        <Grid p={rem(12)}>
            <Grid.Col span={3} pr={rem(15)}>
                <Flex direction={'row'} gap={rem(12)}>
                    <Avatar color={'initials'} name={props.name} radius="xl" />
                    <Flex direction='column' gap={rem(6)}>
                    <Text fw={700} fz={rem(15)} lh={rem(24)} c={'#1A1D1F'}>{props.name}</Text>
                        <Text fw={600} fz={rem(13)} lh={rem(16)} c={'#7C7F82'} lineClamp={5}>
                            {props.fullAddress}
                        </Text>
                        <Button maw={rem(130)} size={'sm'} bg={'#FCFCFC'} mt={rem(6)} c={'#1A1D1F'}
                                styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                                component={'a'} href={`https://bconnectplatform.lightning.force.com/lightning/r/Contact/${props.salesforceId}/view`}
                                radius={rem(8)}
                                target="_blank"
                                rel="noopener noreferrer">Contact Info</Button>
                    </Flex>
                </Flex>
            </Grid.Col>
            <Grid.Col span={6} px={rem(15)}>
                <Flex direction={'column'} gap={rem(16)}>
                    <Grid>
                      <Grid.Col span={2}>
                          <Badge
                              c={props.daysWaiting > 180 ? '#E1251B' : props.daysWaiting > 90 ? '#F59E0B' : '#272B30'}
                              bg={props.daysWaiting > 180 ? '#F9E6E5' : props.daysWaiting > 90 ? '#FEF3C7' : '#EFEFEF'}
                              fw={600}
                              fz={rem(15)}
                              lh={rem(24)}
                              tt={'capitalize'}
                              p={rem(8)}
                              radius="sm"
                              size={'lg'}
                          >{props.daysWaiting}</Badge>
                      </Grid.Col>
                        <Grid.Col span={2}>
                            <Text
                                c={'#6F767E'}
                                fw={600}
                                fz={rem(14)}
                                lh={rem(24)}
                            >{props.age}</Text>
                      </Grid.Col>
                      <Grid.Col span={5}>
                          <Text
                              c={'#6F767E'}
                              fw={600}
                              fz={rem(13)}
                              lh={rem(17)}
                          >{ availability }</Text>
                      </Grid.Col>
                      <Grid.Col span={3}>
                          <Badge
                              c={'#1A1D1F'}
                              bg={'#EFEFEF'}
                              fw={600}
                              fz={rem(15)}
                              lh={rem(24)}
                              tt={'capitalize'}
                              p={rem(8)}
                              radius="sm"
                              size={'lg'}
                          >{props.gender}</Badge>
                      </Grid.Col>
                    </Grid>

                    <Flex direction={'row'} gap={rem(4)}>
                        {generalInformationItems.map((item, index) => (
                            <Badge
                              key={index}
                              px={rem(8)}
                              py={rem(4)}
                              c={'#272B30'}
                              bg={'#EFEFEF'}
                              fw={600}
                              fz={rem(12)}
                              lh={rem(16)}
                              tt={'capitalize'}
                              radius="sm"
                              size={'lg'}
                            >{item}</Badge>
                        ))}
                    </Flex>
                    <Flex direction={'row'} gap={rem(4)}>
                        {locationItems.map((item, index) => (
                            <Badge
                              key={index}
                              px={rem(8)}
                              py={rem(4)}
                              c={'#272B30'}
                              bg={'#EFEFEF'}
                              fw={500}
                              fz={rem(12)}
                              lh={rem(16)}
                              tt={'capitalize'}
                              radius="sm"
                              size={'lg'}
                            >{item}</Badge>
                        ))}
                    </Flex>
                </Flex>
            </Grid.Col>
            <Grid.Col span={2} px={rem(15)}>
                <Flex direction={'column'} gap={rem(12)}>
                    <Badge
                        px={rem(10)}
                        py={rem(6)}
                        c={'#fff'}
                        bg={'#249C4D'}
                        fw={600}
                        fz={rem(14)}
                        lh={rem(24)}
                        radius="md"
                        size={'xl'}
                        w={'100%'}
                        maw={140}
                        tt={"capitalize"}
                    >
                        {props.volunteerMatches} Matches
                    </Badge>

                    <Button maw={140} size={'sm'} bg={'#FCFCFC'} mt={rem(6)}
                                styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                                radius={rem(8)} py={rem(4)} component='a' href={`/map/older_person/${props.id}`} target="_blank" rel="noopener noreferrer">
                        <Text c={'#6F767E'} fz={rem(12)} lh={rem(24)} fw={700}>Map</Text>
                    </Button>
                </Flex>
            </Grid.Col>
            <Grid.Col span={1} pl={rem(15)}>
                <ActionIcon variant="transparent" color="#6F767E" size="xl" aria-label="Settings" onClick={() => props.onClick()}>
                  <IconChevronRight stroke={2} />
                </ActionIcon>
            </Grid.Col>
        </Grid>
    )
}